import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Card,
    CardContent, IconButton, Fade, Grow, Zoom, useTheme, useMediaQuery, Menu, MenuItem } from '@mui/material';
import { Phone, Mail, MapPin, Award, Shield, TrendingUp, Star, Package, Menu as MenuIcon } from 'lucide-react';
import api from '../utils/api';
import Footer from './Footer';
import LazyImage from './LazyImage';

const HeroSection = ({ onExploreClick }) => (
    <Box
        sx={{
            position: 'relative',
            minHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                animation: 'pulse 8s ease-in-out infinite',
            },
            '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
            }
        }}
    >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Fade in timeout={1000}>
                        <Box>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'white',
                                    mb: 3,
                                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                                }}
                            >
                                Premium Quality Food Products
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    mb: 4,
                                    lineHeight: 1.6,
                                }}
                            >
                                Serving excellence since 1966. Your trusted partner for quality grains, pulses, and food products.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={onExploreClick}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#667eea',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: '#f0f0f0',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                        },
                                        transition: 'all 0.3s',
                                    }}
                                >
                                    Explore Products
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    href="#contact"
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'white',
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s',
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </Box>
                        </Box>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Zoom in timeout={1200}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                component="img"
                                src="/Brand_logo.svg"
                                alt="Brand Logo"
                                sx={{
                                    width: '100%',
                                    maxWidth: '400px',
                                    p: 4,
                                    borderRadius: 4,
                                    backgroundColor: 'white',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                                    animation: 'float 6s ease-in-out infinite',
                                    '@keyframes float': {
                                        '0%, 100%': { transform: 'translateY(0px)' },
                                        '50%': { transform: 'translateY(-20px)' },
                                    }
                                }}
                            />
                        </Box>
                    </Zoom>
                </Grid>
            </Grid>
        </Container>
    </Box>
);

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
    <Grow in timeout={1000 + delay}>
        <Card
            sx={{
                textAlign: 'center',
                p: 4,
                height: '100%',
                borderRadius: 3,
                transition: 'all 0.3s',
                '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    color: 'white',
                }}
            >
                <Icon size={40} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Card>
    </Grow>
);

const TestimonialCard = ({ name, role, content, rating }) => (
    <Card
        sx={{
            p: 4,
            height: '100%',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }
        }}
    >
        <Box sx={{ display: 'flex', mb: 2 }}>
            {[...Array(rating)].map((_, i) => (
                <Star key={i} size={20} fill="#ffa726" color="#ffa726" />
            ))}
        </Box>
        <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', lineHeight: 1.8 }}>
            "{content}"
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
                sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                }}
            >
                {name.charAt(0)}
            </Box>
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {role}
                </Typography>
            </Box>
        </Box>
    </Card>
);

const CategoryCard = ({ category, onClick }) => {

    return (
        <Zoom in timeout={600}>
            <Card
                sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-10px)',
                        cursor: 'pointer',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    },
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}
                onClick={onClick}
            >
                <Box
                    sx={{
                        position: 'relative',
                        paddingTop: '75%',
                        width: '100%',
                        overflow: 'hidden'
                    }}
                >
                    <LazyImage
                        src={category.image?.url}
                        alt={category.name}
                        height="100%"
                        showPlaceholderIcon={!category.image?.url}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            '&:hover': {
                                transform: 'scale(1.15)'
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6))',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            '&:hover': { opacity: 1 }
                        }}
                    />
                </Box>
                <CardContent
                    sx={{
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: 3
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2,
                            height: '2.4em',
                        }}
                    >
                        {category.name}
                    </Typography>
                    {category.description && (
                        <Typography
                            variant="body2"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                height: '4.5em',
                                lineHeight: 1.5,
                                opacity: 0.9,
                            }}
                        >
                            {category.description}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Zoom>
    );
};

const ContactInfo = () => (
    <Box id="contact" sx={{ py: 10, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: '#fff' }}>
        <Container maxWidth="lg">
            <Typography variant="h3" align="center" fontWeight="bold" gutterBottom>
                Get In Touch
            </Typography>
            <Typography variant="h6" align="center" sx={{ mb: 6}}>
                We would love to hear from you
            </Typography>
            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Fade in timeout={1000}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                p: 4,
                                bgcolor: 'rgba(255,255,255,0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}
                            >
                                <Phone size={32} />
                            </Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#fff' }}>Phone</Typography>
                            <Typography sx = {{ color: '#fff' }}>+91 9351375631</Typography>
                            <Typography sx = {{ color: '#fff' }}>+91 9414247406</Typography>
                        </Card>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Fade in timeout={1200}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                p: 4,
                                bgcolor: 'rgba(255,255,255,0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}
                            >
                                <Mail size={32} />
                            </Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#fff' }}>Email</Typography>
                            <Typography sx = {{ color: '#fff' }}>mangluniasajendra@gmail.com</Typography>
                            <Typography sx = {{ color: '#fff' }}>nileshmanglunia@gmail.com</Typography>
                        </Card>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Fade in timeout={1400}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                p: 4,
                                bgcolor: 'rgba(255,255,255,0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}
                            >
                                <MapPin size={32} />
                            </Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#fff' }}>Address</Typography>
                            <Typography sx={{ color: '#fff' }}>Near Jain School, Natho Talab</Typography>
                            <Typography sx={{ mb: 2, color: '#fff' }}>Sujangarh, Rajasthan - 331507</Typography>
                            <Button
                                variant="outlined"
                                href="https://maps.app.goo.gl/7HVJLrdKEjRqftKk7"
                                target="_blank"
                                sx={{
                                    borderColor: 'white',
                                    color: 'white',
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    }
                                }}
                            >
                                View on Map
                            </Button>
                        </Card>
                    </Fade>
                </Grid>
            </Grid>
        </Container>
    </Box>
);

const LandingPage = () => {
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const categoriesRef = React.useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigate = (path) => {
        navigate(path);
        handleMenuClose();
    };
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setError('Failed to load categories');
        }
    };

    const handleCategoryClick = (categoryId) => {
        navigate(`/category/${categoryId}`);
    };

    const scrollToCategories = () => {
        categoriesRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const features = [
        {
            icon: Award,
            title: 'Premium Quality',
            description: 'Handpicked products sourced from the finest farms across India, ensuring superior quality in every grain.',
            delay: 0
        },
        {
            icon: Shield,
            title: 'Trusted Since 1966',
            description: 'Over 60 years of excellence in delivering quality food products with unwavering commitment to customer satisfaction.',
            delay: 200
        },
        {
            icon: TrendingUp,
            title: 'Best Prices',
            description: 'Competitive pricing without compromising on quality. Get the best value for your money with every purchase.',
            delay: 400
        },
    ];

    const testimonials = [
        {
            name: 'Rajesh Kumar',
            role: 'Restaurant Owner',
            content: 'The quality of products is outstanding. We have been ordering from Ramdayal Rameshwar Lal for over 10 years and they never disappoint.',
            rating: 5
        },
        {
            name: 'Priya Sharma',
            role: 'Home Chef',
            content: 'Excellent quality grains and pulses. The freshness is remarkable and delivery is always on time. Highly recommended!',
            rating: 5
        },
        {
            name: 'Amit Patel',
            role: 'Grocery Store Owner',
            content: 'Reliable supplier with consistent quality. Their customer service is exceptional and prices are very competitive.',
            rating: 5
        },
    ];

    return (
        <Box sx={{ bgcolor: '#f8f9fa' }}>
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                py: 2,
                position: 'sticky',
                top: 0,
                zIndex: 100,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                <Container maxWidth="lg">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexGrow: 1,     
                            minWidth: 0           // allows text wrapping
                        }}
                        >
                            <img
                                src="/Brand_logo.svg"
                                alt="Brand Logo"
                                style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    height: '64px',
                                    padding: '4px'
                                }}
                            />
                            <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', sm: '2rem' }, // bigger than before
                                lineHeight: 1.15,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            }}
                            >
                            Ramdayal Rameshwar Lal
                            </Typography>
                        </Box>

                        {isMobile ? (
                            <>
                                <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleMenuOpen}
                                sx={{ mr: 1 }}
                                >
                                <MenuIcon />
                                </IconButton>

                                <Button
                                variant="contained"
                                onClick={() => navigate('/login')}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#667eea',
                                    fontSize: '0.85rem',
                                    px: 2,
                                    '&:hover': {
                                    bgcolor: '#f0f0f0',
                                    }
                                }}
                                >
                                Login
                                </Button>

                                <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                sx={{
                                    '& .MuiPaper-root': {
                                    backgroundColor: '#1a1a2e',
                                    color: 'white',
                                    borderRadius: '8px',
                                    }
                                }}
                                >
                                <MenuItem onClick={() => handleNavigate('/about')}>About</MenuItem>
                                <MenuItem onClick={() => handleNavigate('/products')}>Products</MenuItem>
                                </Menu>
                            </>
                            ) : (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    onClick={() => navigate('/about')}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                        }
                                    }}
                                >
                                    About
                                </Button>
                                <Button
                                    onClick={() => navigate('/products')}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                        }
                                    }}
                                >
                                    Products
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/login')}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#667eea',
                                        '&:hover': {
                                            bgcolor: '#f0f0f0',
                                        }
                                    }}
                                >
                                    Login
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* Hero Section */}
            <HeroSection onExploreClick={scrollToCategories} />

            {/* Features Section */}
            <Box sx={{ py: 10, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Why Choose Us
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}>
                        Experience the difference of quality, trust, and value
                    </Typography>
                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <FeatureCard {...feature} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Categories Section */}
            <Box ref={categoriesRef} sx={{ py: 10, bgcolor: '#f8f9fa' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        align="center"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                        }}
                    >
                        Our Product Categories
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}>
                        Explore our wide range of premium food products
                    </Typography>
                    {error ? (
                        <Typography color="error" align="center">{error}</Typography>
                    ) : (
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                alignItems: 'stretch',
                            }}
                        >
                            {categories.map((category) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={category._id}
                                    sx={{ display: 'flex' }}
                                >
                                    <CategoryCard
                                        category={category}
                                        onClick={() => handleCategoryClick(category._id)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/products')}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                },
                                transition: 'all 0.3s',
                            }}
                        >
                            View All Products
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Testimonials Section */}
            <Box sx={{ py: 10, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
                        What Our Customers Say
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}>
                        Don't just take our word for it
                    </Typography>
                    <Grid container spacing={4}>
                        {testimonials.map((testimonial, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Fade in timeout={1000 + index * 200}>
                                    <div>
                                        <TestimonialCard {...testimonial} />
                                    </div>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 10,
                    textAlign: 'center',
                }}
            >
                <Container maxWidth="md">
                    <Package size={64} style={{ marginBottom: '24px' }} />
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                        Ready to Experience Quality?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 5, opacity: 0.95 }}>
                        Join thousands of satisfied customers who trust us for their daily needs
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/products')}
                            sx={{
                                bgcolor: 'white',
                                color: '#667eea',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    bgcolor: '#f0f0f0',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                },
                                transition: 'all 0.3s',
                            }}
                        >
                            Explore Products
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            href="#contact"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s',
                            }}
                        >
                            Contact Us
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Contact Section */}
            <ContactInfo />

            {/* Footer */}
            <Footer />
        </Box>
    );
};

export default LandingPage;